import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Target, Users, Lightbulb, Award } from 'lucide-react';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  enter: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

function AboutPage() {
  const values = [
    {
      icon: Target,
      title: 'Resultatdrevet',
      description: 'Vi fokuserer på konkrete resultater, der gør en forskel for jeres bundlinje.'
    },
    {
      icon: Users,
      title: 'Partnerskab',
      description: 'Vi arbejder sammen med jer – ikke bare for jer. Ægte samarbejde skaber de bedste løsninger.'
    },
    {
      icon: Lightbulb,
      title: 'Nytænkning',
      description: 'Vi holder os opdateret på de nyeste teknologier, så I altid får de bedste løsninger.'
    },
    {
      icon: Award,
      title: 'Kvalitet',
      description: 'Vi går ikke på kompromis med kvaliteten. Hver løsning vi leverer, er vi stolte af.'
    }
  ];

  const stats = [
    { value: '3+', label: 'Founders' },
    { value: '100%', label: 'Commitment' }
  ];

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="enter"
      exit="exit"
      transition={{ duration: 0.4 }}
    >
      {/* Hero */}
      <section className="section" style={{ paddingTop: 'calc(var(--header-height) + var(--space-16))' }}>
        <div className="container">
          <motion.div
            className="section__header"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="section__tag">HVEM ER VI</span>
            <h1 className="section__title">Om Core4it</h1>
            <p className="section__description">
              Core4it blev grundlagt af tre passionerede udviklere med en fælles overbevisning: 
              teknologi skal gøre hverdagen lettere – ikke sværere.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main About */}
      <section className="section section--alt">
        <div className="container">
          <div className="about-grid">
            <motion.div
              className="about__content"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="about__title">
                Teknologi som <span className="gradient-text">fordel</span>
              </h2>
              
              <p className="about__text">
               Vi kombinerer erfaring fra industrien med en hands-on tilgang.
               Vi leverer ikke bare kode – vi samarbejder med jer for at forstå 
               jeres forretning og bygge løsninger, der skaber konkrete resultater.
               Med et lille, dedikeret team leverer vi den fleksibilitet og det personlige engagement,
               som større leverandører sjældent kan matche.
              </p>

              <div className="about__stats">
                {stats.map((stat) => (
                  <div key={stat.label} className="about__stat">
                    <span className="about__stat-value">{stat.value}</span>
                    <span className="about__stat-label">{stat.label}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="about__image-wrapper"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="about__image-bg" />
              <img
                src="/images/team.jpg"
                alt="Core4it team collaborating"
                className="about__image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section">
        <div className="container">
          <motion.div
            className="section__header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="section__tag">Vores tilgang</span>
            <h2 className="section__title">Det der driver os</h2>
            <p className="section__description">
              Vores værdier guider hvert eneste projekt og samarbejde.
            </p>
          </motion.div>

          <div className="services-grid">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                className="service-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <div className="service-card__icon service-card__icon--blue">
                  <value.icon />
                </div>
                <h3 className="service-card__title" style={{ fontSize: 'var(--text-xl)' }}>
                  {value.title}
                </h3>
                <p className="service-card__text">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section section--alt">
        <div className="container">
          <motion.div
            className="section__header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="section__title">Mød teamet</h2>
            <p className="section__description">
              Lær folkene bag Core4it at kende.
            </p>
            <div style={{ marginTop: 'var(--space-8)' }}>
              <Link to="/team" className="btn btn--primary btn--large">
                Se vores team
                <ArrowRight className="btn__icon" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
}

export default AboutPage;
